const MS_PER_DAY = 86_400_000

export type ZonedYmd = {
  year: number
  month: number
  day: number
}

export const getBrowserTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

export const isValidTimeZone = (timeZone: string): boolean => {
  if (!timeZone || typeof timeZone !== 'string') return false
  try {
    // eslint-disable-next-line no-new
    new Intl.DateTimeFormat('en-US', { timeZone })
    return true
  } catch {
    return false
  }
}

export const resolveTimeZone = (preferred?: string): string => {
  if (preferred && isValidTimeZone(preferred)) return preferred

  const browser = getBrowserTimeZone()
  if (browser && isValidTimeZone(browser)) return browser

  return 'UTC'
}

export const getTimeZoneFromFarm = (farm: unknown): string | undefined => {
  if (!farm || typeof farm !== 'object') return undefined
  const metadata = (farm as any).metadata
  if (!metadata || typeof metadata !== 'object') return undefined

  const candidates = [
    (metadata as any).timezone,
    (metadata as any).timeZone,
    (metadata as any).tz
  ]

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim().length > 0) {
      return candidate.trim()
    }
  }

  return undefined
}

export const getZonedYmd = (value: Date | string, timeZone: string): ZonedYmd | null => {
  if (typeof value === 'string') {
    const trimmed = value.trim()
    // A bare date, the UTC-midnight "calendar day" sentinel and a wall-clock
    // datetime-local value (no zone) all name a day already: never shift them
    // through a time zone (findings #85 — west of UTC they read a day early).
    const dateOnlyMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})(?:T00:00(?::00(?:\.0+)?)?Z|T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?)?$/)
    if (dateOnlyMatch) {
      const year = Number(dateOnlyMatch[1])
      const month = Number(dateOnlyMatch[2])
      const day = Number(dateOnlyMatch[3])

      if (
        Number.isFinite(year)
        && Number.isFinite(month)
        && Number.isFinite(day)
        && month >= 1
        && month <= 12
        && day >= 1
        && day <= 31
      ) {
        return { year, month, day }
      }
    }
  }

  const date = typeof value === 'string' ? new Date(value) : value
  if (Number.isNaN(date.getTime())) return null

  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  })

  const parts = formatter.formatToParts(date)
  const year = Number(parts.find((part) => part.type === 'year')?.value)
  const month = Number(parts.find((part) => part.type === 'month')?.value)
  const day = Number(parts.find((part) => part.type === 'day')?.value)

  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null

  return {
    year,
    month,
    day
  }
}

export const getZonedIsoDate = (value: Date | string, timeZone: string): string | null => {
  const ymd = getZonedYmd(value, timeZone)
  if (!ymd) return null
  return `${ymd.year}-${String(ymd.month).padStart(2, '0')}-${String(ymd.day).padStart(2, '0')}`
}

export const getZonedWeekKey = (value: Date | string, timeZone: string): string | null => {
  const ymd = getZonedYmd(value, timeZone)
  if (!ymd) return null

  const dateUtc = new Date(Date.UTC(ymd.year, ymd.month - 1, ymd.day))
  const yearStartUtc = new Date(Date.UTC(ymd.year, 0, 1))
  const diffDays = ((dateUtc.getTime() - yearStartUtc.getTime()) / MS_PER_DAY) + yearStartUtc.getUTCDay() + 1
  const weekNum = Math.ceil(diffDays / 7)

  if (!Number.isFinite(weekNum)) return null
  return `${ymd.year}-W${String(weekNum).padStart(2, '0')}`
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export const formatZonedWeekDisplay = (value: Date | string, weekKey: string, timeZone: string): string => {
  const ymd = getZonedYmd(value, timeZone)
  if (!ymd) return weekKey

  const monthName = MONTH_NAMES[Math.max(0, Math.min(11, ymd.month - 1))] ?? 'Jan'
  const weekPart = weekKey.split('-W')[1] ? `W${weekKey.split('-W')[1]}` : weekKey
  return `${ymd.year} ${monthName} ${ymd.day} - ${weekPart}`
}
